/**
 * Settings.tsx
 * Comprehensive settings page with tabs for different setting categories.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Snackbar,
  IconButton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { useConsultantBranding } from '../contexts/ConsultantBrandingContext';
import { useThemeMode } from '../contexts/ThemeContext';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface ConsultantSettings {
  company_display_name: string;
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  company_tagline: string;
  email_on_audit_complete: boolean;
  email_on_action_item_assigned: boolean;
  email_on_action_item_overdue: boolean;
  email_weekly_summary: boolean;
  default_audit_due_days: number;
  default_action_item_priority: string;
  default_billing_responsibility: string;
  allow_client_self_service: boolean;
}

const Settings: React.FC = () => {
  const { user, resetPassword } = useAuth();
  const { selectedCompany } = useCompany();
  const { refreshBranding } = useConsultantBranding();
  const { mode: themeMode, setThemeMode } = useThemeMode();
  
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Settings state
  const [settings, setSettings] = useState<ConsultantSettings>({
    company_display_name: '',
    logo_url: '',
    primary_color: '#E53935',
    secondary_color: '#212121',
    company_tagline: '',
    email_on_audit_complete: true,
    email_on_action_item_assigned: true,
    email_on_action_item_overdue: true,
    email_weekly_summary: false,
    default_audit_due_days: 30,
    default_action_item_priority: 'medium',
    default_billing_responsibility: 'consultant',
    allow_client_self_service: false,
  });
  
  // Security settings state (password form no longer needed - using email reset flow)

  useEffect(() => {
    loadSettings();
  }, [selectedCompany]);

  const loadSettings = async () => {
    if (!selectedCompany) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await api.get(`/companies/${selectedCompany.id}/settings/`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (err: any) {
      // Settings might not exist yet, that's okay
      console.log('No existing settings found, using defaults');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!selectedCompany) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      await api.put(`/companies/${selectedCompany.id}/settings/`, settings);
      setSuccess('Settings saved successfully!');
      
      // Refresh branding context to update header
      await refreshBranding();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) {
      setError('User email not found');
      return;
    }
    
    try {
      setIsSaving(true);
      setError(null);
      await resetPassword(user.email);
      setSuccess('Password reset email sent! Check your inbox for the reset link.');
    } catch (err: any) {
      // For security, show success even if email doesn't exist
      setSuccess('Password reset email sent! Check your inbox for the reset link.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account and business preferences
        </Typography>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<SettingsIcon />} label="General" iconPosition="start" />
          <Tab icon={<NotificationsIcon />} label="Notifications" iconPosition="start" />
          <Tab icon={<SecurityIcon />} label="Security" iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* General Settings */}
          <TabPanel value={activeTab} index={0}>
            <Typography variant="h6" sx={{ mb: 3 }}>General Settings</Typography>
            
            {/* Theme Mode - Functional */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Theme Mode
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose between light and dark theme. Dark mode matches the landing page aesthetic with glassmorphism effects.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton 
                      onClick={() => setThemeMode('light')}
                      color={themeMode === 'light' ? 'primary' : 'default'}
                      sx={{ 
                        border: themeMode === 'light' ? '2px solid' : '1px solid',
                        borderColor: themeMode === 'light' ? 'primary.main' : 'divider'
                      }}
                      title="Light Mode"
                    >
                      <LightModeIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => setThemeMode('dark')}
                      color={themeMode === 'dark' ? 'primary' : 'default'}
                      sx={{ 
                        border: themeMode === 'dark' ? '2px solid' : '1px solid',
                        borderColor: themeMode === 'dark' ? 'primary.main' : 'divider'
                      }}
                      title="Dark Mode (Default)"
                    >
                      <DarkModeIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Alert severity="success">
              Theme changes are applied instantly and saved to your user profile. Your preference will sync across all your devices.
            </Alert>
          </TabPanel>

          {/* Notification Settings */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="h6" sx={{ mb: 3 }}>Email Notifications</Typography>
            
            {/* Disabled notification options */}
            <Box sx={{ position: 'relative' }}>
              {/* Coming Soon Overlay - only covers notification switches */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(1px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: 2,
                }}
              >
                <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                      Coming Soon
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Email notification preferences will be available in a future update.
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.email_on_audit_complete}
                      disabled
                    />
                  }
                  label={
                    <Box>
                      <Typography>Review Completion</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Receive email when an audit is completed
                      </Typography>
                    </Box>
                  }
                />
                
                <Divider />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.email_on_action_item_assigned}
                      disabled
                    />
                  }
                  label={
                    <Box>
                      <Typography>Action Item Assignment</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Receive email when action items are assigned to you
                      </Typography>
                    </Box>
                  }
                />
                
                <Divider />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.email_on_action_item_overdue}
                      disabled
                    />
                  }
                  label={
                    <Box>
                      <Typography>Overdue Reminders</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Receive email when action items become overdue
                      </Typography>
                    </Box>
                  }
                />
                
                <Divider />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.email_weekly_summary}
                      disabled
                    />
                  }
                  label={
                    <Box>
                      <Typography>Weekly Summary</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Receive a weekly summary of audit activity
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
            
            {/* Save button outside overlay container */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled
              >
                Save Changes
              </Button>
            </Box>
          </TabPanel>

          {/* Security Settings */}
          <TabPanel value={activeTab} index={2}>
            <Typography variant="h6" sx={{ mb: 3 }}>Security Settings</Typography>
            
            {/* Reset Password */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Reset Password
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  To change your password, we'll send you a secure reset link via email. 
                  This ensures your account stays protected.
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Current email:</strong> {user?.email}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    You'll receive a reset link at this address. The link will expire in 24 hours.
                  </Typography>
                </Alert>
                
                <Button
                  variant="contained"
                  onClick={handlePasswordReset}
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
                >
                  {isSaving ? 'Sending Reset Link...' : 'Send Password Reset Email'}
                </Button>
              </CardContent>
            </Card>
            
            {/* Activity Log Link */}
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Activity Log
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  View your recent account activity and login history
                </Typography>
                <Button variant="outlined" href="/activity-log">
                  View Activity Log
                </Button>
              </CardContent>
            </Card>
          </TabPanel>

        </Box>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" variant="filled">
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;

